<template>
  <div style="display: inline">
    <component
      :is="slideoverComponent"
      :title="slideoverTitle"
      :visible="slideoverVisible"
      v-bind="slideoverProperties"
      @toggle-slideover="toggleSlideover"
      @refresh-order="refreshOrder"
    />

    <div class="pb-3 flex flex-row justify-end" style="height: 40px">
      <ActionsMenu v-if="canShowActions">
        <ActionsItem
          :disabled="!hasEditableItems"
          @click="toggleSlideover('OrderEditSlideoverVBI', 'Edit Products')"
        >
          Edit Products
        </ActionsItem>
        <ActionsItem
          v-if="userCanEditPrintDates && !restrictedOrder"
          @click="
            toggleSlideover(
              'OrderEditPrintDatesVBISlideover',
              'Edit Print Dates'
            )
          "
        >
          Edit Print Dates
        </ActionsItem>
        <ActionsItem
          v-if="userCanReviseItem"
          :disabled="!hasSelectedItem || !itemIsRevisable"
          @click="
            toggleSlideover('ReviseLineItemSlideover', 'Revise Item', {
              itemToBeRevised: selectedItem
            })
          "
        >
          Revise Item
        </ActionsItem>
        <ActionsItem
          v-if="userCanSelectFacility"
          @click="
            toggleSlideover('SelectFacilityVBISlideover', 'Select Facility', {
              shipState: order.ShipState
            })
          "
        >
          Select Facility
        </ActionsItem>
      </ActionsMenu>
    </div>

    <div>
      <ag-grid-vue
        style="width: 100%; height: 280px"
        class="ag-theme-balham"
        :columnDefs="columnDefs"
        :rowData="rowData"
        :paginationAutoPageSize="false"
        :paginationPageSize="paginationPageSize"
        :pagination="true"
        id="orderDetails"
        :gridOptions="gridOptions"
        @grid-ready="onGridReady"
        :context="context"
        :frameworkComponents="frameworkComponents"
        :defaultColDef="defaultColDef"
        :enableCellTextSelection="true"
        :ensureDomOrder="true"
      >
      </ag-grid-vue>
    </div>
  </div>
</template>

<script>
import auth from "@/api/auth";
import agGrid from "@/mixins/agGrid";
import { AgGridVue } from "ag-grid-vue";
import dateFormatter from "@/mixins/dateFormatter";
import moneyFormatter from "@/mixins/moneyFormatter";
import Button from "@/components/elements/Button.vue";
import { date } from "@/components/agGrid/cellRenderers";
import SelectFacilityVBISlideover from "./slideovers/VBI/SelectFacilityVBI.vue";
import OrderEditSlideoverVBI from "./slideovers/OrderEdit/VBI/OrderEdit.vue";
import ForceBackorderSlideover from "./slideovers/OrderEdit/ForceBackorder.vue";
import OrderEditPrintDatesVBISlideover from "@/views/Customers/components/slideovers/VBI/OrderEditPrintDatesVBI";
import ReviseLineItemSlideover from "@/views/Customers/components/slideovers/OrderEdit/ReviseLineItem";

export default {
  name: "OrderProductTable",
  components: {
    Button,
    AgGridVue,
    OrderEditSlideoverVBI,
    ForceBackorderSlideover,
    OrderEditPrintDatesVBISlideover,
    SelectFacilityVBISlideover,
    ReviseLineItemSlideover
  },
  mixins: [agGrid, moneyFormatter, dateFormatter],
  props: {
    details: {
      type: [Object, Array],
      required: true
    }
  },
  data() {
    return {
      selectedItem: {},
      columnDefs: null,
      rowData: null,
      paginationPageSize: 100,
      frameworkComponents: null,
      gridApi: null,
      columnApi: null,
      gridOptions: null,
      context: null,
      defaultColDef: null,
      overlayLoadingTemplate: true,
      showProductActionsMenu: false,
      slideoverTitle: "",
      slideoverVisible: false,
      slideoverComponent: null,
      slideoverProperties: {}
    };
  },
  computed: {
    order() {
      return this.$store.getters["orders/get"]("order");
    },
    canShowActions() {
      if (this.order.status?.Status === "Unprocessed") {
        return false;
      }

      return !this.orderIsCancelled;
    },
    restrictedOrder() {
      return ["Restricted"].includes(this.order.status?.Status);
    },
    orderWasPlacedOnShopify() {
      return [22, 23, 31, 32].indexOf(Number(this.order.CartID)) > -1;
    },
    orderIsCancelled() {
      return Number(this.order.Cancelled);
    },
    userCanAddItem() {
      if (this.order.status?.Status === "Complete") {
        return false;
      }

      if (auth.isUserAuthorized("admin")) {
        return true;
      }

      if (
        !this.orderWasPlacedOnShopify &&
        auth.isUserAuthorized("third-party")
      ) {
        return false;
      }

      return auth.isUserAuthorized(["supervisor", "third-party"]);
    },
    userCanReviseItem() {
      return auth.isUserAuthorized(["supervisor", "third-party"]);
    },
    userCanSelectFacility() {
      return auth.isUserAuthorized("admin");
    },
    userCanCancelItem() {
      return auth.isUserAuthorized("admin");
    },
    userCanEditPrintDates() {
      return auth.isUserAuthorized(["supervisor", "third-party"]);
    },
    hasSelectedItem() {
      return Boolean(this.selectedItem.ItemNumber);
    },
    selectedItemProductType() {
      return this.selectedItem?.variant?.product?.type;
    },
    itemIsRevisable() {
      return (
        !this.selectedItemHasDateShipped &&
        !this.selectedItemIsKitPart &&
        !Number(this.selectedItem?.Canceled) &&
        this.selectedItemProductType !== "INTERNAL"
      );
    },
    itemIsCancellable() {
      return (
        (!this.selectedItemHasDateShipped &&
          !this.selectedItemIsKitPart &&
          !this.selectedItemIsCancelled) ||
        this.selectedItemIsRestricted
      );
    },
    selectedItemIsRestricted() {
      return Boolean(
        this.selectedItem.ApprovalStatus?.toUpperCase() == "RESTRICTED"
      );
    },
    selectedItemIsKitPart() {
      return Boolean(this.selectedItem.Type == "P");
    },
    selectedItemHasDateShipped() {
      return Boolean(this.selectedItem.DateShipped);
    },
    selectedItemIsCancelled() {
      return (
        this.selectedItem?.Canceled == 1 ||
        this.selectedItem?.Product?.search(/^CANCELED/g) > -1
      );
    },
    orderCanBeFulfilled() {
      return !this.orderIsCancelled && this.hasItemsInBackorder;
    },
    orderCanBeForcedToBackorder() {
      return !this.orderIsCancelled && this.hasBackorderableItems;
    },
    hasItemsInBackorder() {
      return Boolean(
        this.order.details.reduce(
          (accumulator, detail) =>
            Number(accumulator) + Number(detail.QuantityNeeded),
          0
        )
      );
    },
    hasBackorderableItems() {
      return Boolean(
        this.details.filter(detail => {
          return (
            this.detailIsNotAdjustment(detail) &&
            this.detailHasQuantityShipped(detail) &&
            this.detailIsNotDropShip(detail) &&
            this.detailDoesNotHaveParent(detail) &&
            this.detailHasNotBeenShipped(detail)
          );
        }).length
      );
    },
    hasEditableItems() {
      return Boolean(
        this.details.filter(detail => {
          return (
            this.detailIsNotAdjustment(detail) &&
            this.detailDoesNotHaveParent(detail) &&
            this.detailHasNotBeenShipped(detail) &&
            this.detailHasNotBeenFulfilled(detail)
          );
        }).length
      );
    }
  },
  async beforeMount() {
    this.gridOptions = {
      headerHeight: 30,
      rowHeight: 30,
      pagination: false,
      suppressPaginationPanel: true,
      rowSelection: "single",
      rowDeselection: true,
      onSelectionChanged: this.itemSelectionChanged,
      getRowStyle(params) {
        if (params.data.DropShip == "1") {
          return { "background-color": "blue", color: "white" };
        }
        if (params.data.Type === "K") {
          return { "background-color": "grey", color: "white" };
        }
        if (params.data.Type === "P") {
          return { "background-color": "#aaa" };
        }
      }
    };
    this.context = { componentParent: this };

    this.defaultColDef = {
      sortable: true,
      filter: true,
      flex: 0,
      suppressMovable: false
    };

    this.frameworkComponents = {
      dateCell: date
    };

    this.columnDefs = [
      {
        headerName: "Guarantee Warranty",
        field: "Warranty",
        width: 100,
        filter: false,
        sortable: false
      },
      {
        headerName: "Qty",
        field: "QuantityOrdered",
        width: 80,
        filter: false,
        sortable: false
      },
      {
        headerName: "BO",
        field: "QuantityNeeded",
        width: 80,
        filter: false,
        sortable: false,
        cellClassRules: {
          "bg-red-200": "x > 0 && data.Text1?.length > 0",
          "bg-red-500": "x > 0 && (data.Text1 == '' || data.Text1 == null)"
        }
      },
      {
        headerName: "CO",
        field: "QuantityShipped",
        width: 80,
        filter: false,
        sortable: false
      },
      {
        headerName: "Variant",
        width: 100,
        valueGetter: params => {
          return params.data.variant_id;
        },
        cellRenderer: params => this.restrictedItemCheck(params)
      },
      {
        headerName: "Product",
        width: 200,
        valueGetter: params => {
          return (
            (params.data.Canceled == 1 ? "CANCELED: " : "") +
            params.data.variant.product.name
          );
        },
        tooltipValueGetter: params => {
          return params.data.variant.product.name;
        }
      },
      {
        headerName: "Size",
        valueGetter: params => {
          return params.data.variant.variant_name;
        }
      },
      {
        headerName: "G",
        field: "guaranteeCount",
        width: 50,
        filter: false,
        sortable: false,
        valueGetter: params => {
          if (params.data?.warrantyCounts?.guarantee_count) {
            return params.data?.warrantyCounts?.guarantee_count;
          }
          return 0;
        }
      },
      {
        headerName: "W",
        field: "warrantyCount",
        width: 50,
        filter: false,
        sortable: false,
        valueGetter: params => {
          if (params.data?.warrantyCounts?.warranty_count) {
            return params.data?.warrantyCounts?.warranty_count;
          }
          return 0;
        }
      },
      {
        headerName: "E",
        field: "exceptionCount",
        width: 50,
        filter: false,
        sortable: false,
        valueGetter: params => {
          if (params.data?.warrantyCounts?.exception_count) {
            return params.data?.warrantyCounts?.exception_count;
          }
          return 0;
        }
      },
      {
        headerName: "Original Price",
        field: "PricePerUnit",
        valueGetter: params => {
          return moneyFormatter.format(params.data.PricePerUnit);
        },
        filter: false,
        sortable: false,
        width: 100
      },
      {
        headerName: "EPD",
        valueGetter: params => {
          if (
            Number(params.data.QuantityNeeded) > 0 ||
            Number(params.data.DropShip)
          ) {
            return dateFormatter.format(params.data.ExpectedShipDate);
          }
          return null;
        },
        filter: "agDateColumnFilter",
        filterParams: {
          buttons: ["apply", "reset"],
          suppressAndOrCondition: true
        },
        width: 100
      },
      {
        headerName: "Print Date",
        valueGetter: params => dateFormatter.format(params.data.DateShipped),
        filter: "agDateColumnFilter",
        filterParams: {
          buttons: ["apply", "reset"],
          suppressAndOrCondition: true
        },
        width: 100
      },
      {
        headerName: "EFD",
        valueGetter: params =>
          dateFormatter.format(params.data.FulfillmentDate),
        filter: "agDateColumnFilter",
        filterParams: {
          buttons: ["apply", "reset"],
          suppressAndOrCondition: true
        },
        width: 100
      },
      {
        headerName: "Estimated Cost",
        field: "estimated_cost",
        valueGetter: params => {
          return params.data.variant_estimated_cost;
        },
        filter: false,
        sortable: false,
        width: 100
      },
      {
        headerName: "Facility",
        field: "facility",
        width: 120,
        filter: false,
        sortable: false
      }
    ];
  },
  mounted() {
    this.gridApi = this.gridOptions.api;
    this.gridColumnApi = this.gridOptions.columnApi;
    this.rowData = this.sortProducts(this.details);
  },
  methods: {
    restrictedItemCheck(params) {
      if (params.data.ApprovalStatus === "RESTRICTED") {
        return (
          '<svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" style="display: inline" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">\n' +
          '  <path class="text-red-500" stroke-linecap="round" stroke-linejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />\n' +
          '</svg><div class="tooltip">' +
          params.value +
          '<span class="tooltiptext">State Restricted</span>'
        );
      } else {
        return params.value;
      }
    },
    sortProducts(details) {
      let arr = [];
      details.forEach(detail => {
        if (detail.Type == "K") {
          arr.push(detail);
          details
            .filter(child => child.Parent == detail.ItemNumber)
            .forEach(child => arr.push(child));
        } else if (detail.Type != "P") {
          arr.push(detail);
        }
      });
      return arr;
    },
    onFirstDataRendered(params) {
      params.api.sizeColumnsToFit();
    },
    onGridReady() {},
    shipDate(params) {
      let date = new Date(params.value);
      return `${date
        .getMonth()
        .toString()
        .padStart(2, "0")}/${date
        .getDate()
        .toString()
        .padStart(2, "0")}/${date.getFullYear()}`;
    },
    toggleProductActionsMenu() {
      this.showProductActionsMenu = !this.showProductActionsMenu;
    },
    toggleSlideover(component, title, properties) {
      if (component) {
        this.slideoverComponent = component;
        this.slideoverTitle = title;
        this.slideoverProperties = properties || {};
      }

      setTimeout(() => {
        this.slideoverVisible = !this.slideoverVisible;
      }, 3);
    },
    refreshOrder() {
      this.$router.go(0);
    },
    itemSelectionChanged() {
      let rows = this.gridOptions.api.getSelectedRows();
      if (rows.length !== 1) {
        this.selectedItem = {};
        return;
      }
      this.selectedItem = rows[0];
    },
    detailIsNotAdjustment(detail) {
      return detail.Adjustment == 0;
    },
    detailHasQuantityShipped(detail) {
      return detail.QuantityShipped > 0;
    },
    detailIsNotDropShip(detail) {
      return detail.DropShip == 0;
    },
    detailDoesNotHaveParent(detail) {
      return [null, 0, "0"].indexOf(detail.Parent) >= 0;
    },
    detailHasNotBeenShipped(detail) {
      return [null, ""].indexOf(detail.DateShipped) >= 0;
    },
    detailHasNotBeenFulfilled(detail) {
      return [null, ""].indexOf(detail.FulfillmentDate) >= 0;
    }
  }
};
</script>
<style lang="scss">
@import "../../../../node_modules/ag-grid-community/dist/styles/ag-grid.css";
@import "../../../../node_modules/ag-grid-community/dist/styles/ag-theme-balham.css";

.ag-row-hover {
  background-color: #f3faf7 !important;
  color: black !important;
}
.tooltip {
  position: relative;
  display: inline-block;
}

.tooltip .tooltiptext {
  visibility: hidden;
  width: 120px;
  background-color: #f05252;
  color: #fff;
  text-align: center;
  border-radius: 6px;
  padding: 1px 0;

  /* Position the tooltip */
  position: fixed;
  z-index: 1000;
}

.tooltip:hover .tooltiptext {
  visibility: visible;
}
</style>
